import { type Candidate } from "@vidario/domain";
import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";

import { useVidarioClient } from "../client";
import { queryKeys } from "../queryKeys";

export interface SendToOutreachInput {
  jobId: string;
  candidateIds: readonly string[];
}

/** Batch version of updateCandidateStatus that moves every selected
 * candidate to `outreach`. Drives the MVP_requirements.md §4 Screen 3
 * "Send to Outreach" button — triggers the task mail to OUTREACH_INBOX
 * backend-side.
 *
 * Phase 4 implementation fans out individual PATCHes through the
 * existing endpoint; the backend can replace this with a single
 * dedicated bulk endpoint without any UI change.
 *
 * Uses `Promise.allSettled` so one failed PATCH doesn't discard the
 * writes that succeeded: the fulfilled subset is committed to the cache,
 * and a partial/total failure is re-thrown so the caller's `onError`
 * fires. */
export function useSendToOutreach(): UseMutationResult<Candidate[], Error, SendToOutreachInput> {
  const client = useVidarioClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, candidateIds }) => {
      const results = await Promise.allSettled(
        candidateIds.map((candidateId) =>
          client.updateCandidateStatus({ candidateId, status: "outreach" }),
        ),
      );

      const updated: Candidate[] = [];
      let failed = 0;
      for (const r of results) {
        if (r.status === "fulfilled") updated.push(r.value);
        else failed += 1;
      }

      // Commit the writes that DID land even when siblings failed — a
      // plain Promise.all rejects on the first failure and would throw
      // away every successful write, desyncing the cache from the server.
      for (const c of updated) {
        qc.setQueryData(queryKeys.candidate(jobId, c.id), c);
      }
      if (updated.length > 0) {
        qc.invalidateQueries({ queryKey: ["jobs", jobId, "candidates"] });
        qc.invalidateQueries({ queryKey: ["jobs", "summary"] });
      }

      // Surface partial/total failure so the caller's onError toast fires;
      // the successful writes above are already committed to the cache.
      if (failed > 0) {
        throw new Error(
          updated.length === 0
            ? `Kein Kandidat konnte in Outreach verschoben werden (${failed} fehlgeschlagen).`
            : `${updated.length} von ${candidateIds.length} Kandidaten in Outreach verschoben, ${failed} fehlgeschlagen.`,
        );
      }

      return updated;
    },
  });
}
